import {
  useEffect, useRef, useCallback, useState,
} from 'react'
import isEmpty from 'lodash/isEmpty'

const REFRESH_BEFORE_EXPIRY_SEC = 5 * 60 // 5 minutes in seconds

/**
 * Manages the address widget token lifecycle.
 * - Accepts a getAddressWidgetInfos function that fetches widget information
 * - Returns { widgetToken, apihost, addressWidgetEntry }
 * - Schedules a refresh when 5 minutes or less remain before expiry
 * - Handles token refresh automatically
 */
const useAddressWidgetInfo = getAddressWidgetInfos => {
  const timerRef = useRef(null)
  const [widgetInfo, setWidgetInfo] = useState(null)

  const scheduleRefresh = useCallback(expireTime => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    let safeDelay
    if (expireTime < REFRESH_BEFORE_EXPIRY_SEC) {
      safeDelay = expireTime * 1000
    } else if ((expireTime - REFRESH_BEFORE_EXPIRY_SEC) < REFRESH_BEFORE_EXPIRY_SEC) {
      safeDelay = REFRESH_BEFORE_EXPIRY_SEC * 1000
    } else {
      safeDelay = (expireTime - REFRESH_BEFORE_EXPIRY_SEC) * 1000
    }

    timerRef.current = setTimeout(async () => {
      try {
        const result = await getAddressWidgetInfos()
        if (!isEmpty(result)) {
          const {
            widgetToken, apihost, addressWidgetEntry, expireTime: newExpireTime,
          } = result
          setWidgetInfo({
            widgetToken,
            apihost,
            addressWidgetEntry,
          })
          scheduleRefresh(newExpireTime)
        }
      } catch (e) {
        console.error('Failed to refresh address widget info:', e)
      }
    }, safeDelay)
  }, [getAddressWidgetInfos])

  useEffect(() => {
    if (!getAddressWidgetInfos) return

    let cancelled = false

    const initFetch = async () => {
      try {
        const result = await getAddressWidgetInfos()
        if (cancelled) return

        if (!isEmpty(result)) {
          const {
            widgetToken, apihost, addressWidgetEntry, expireTime,
          } = result
          setWidgetInfo({
            widgetToken,
            apihost,
            addressWidgetEntry,
          })
          scheduleRefresh(expireTime)
        }
      } catch (e) {
        console.error('Failed to fetch address widget info:', e)
      }
    }

    initFetch()

    return () => {
      cancelled = true
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [getAddressWidgetInfos, scheduleRefresh])

  return {
    widgetToken: widgetInfo?.widgetToken,
    apihost: widgetInfo?.apihost,
    addressWidgetEntry: widgetInfo?.addressWidgetEntry,
  }
}

export default useAddressWidgetInfo
